import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { Form, Select, AutoComplete } from "antd"

import { SelectOptionShape, SelectOptionsShape, SelectExtraOptionsShape } from "../FormItem/shapes"

const DEFAULT_SELECT_PLACEHOLDER = "Select an option"
const DEFAULT_SELECT_MULTIPLE_PLACEHOLDER = "Select one or multiple options"


const SelectInput = ({
  form,
  request,
  itemProps,
  isMultiple,
  inputProps,
  options: optionsParam,
  extraOptions,
}) => {
  const [ options, setOptions ] = useState([])
  const [ isLoading, setIsLoading ] = useState(false)
  const {
    minLength,
    maxLength,
    hasAutoComplete = false,
    operationParameters = {},
    onChange: onSelectChange,
    ...otherInputProps
  } = inputProps

  const isArray = Array.isArray(optionsParam)
  const isWatcher = !isArray && typeof optionsParam === "object"
  const isOperation = typeof optionsParam === "string"

  let watchField

  if (isWatcher) {
    const { fieldName: watchPath } = optionsParam
    watchField = Form.useWatch(watchPath, form)
  }

  useEffect(() => {
    if (!isArray) { return }

    setOptions(optionsParam)
  }, [ optionsParam ])

  let isInProgress = false
  useEffect(() => {
    if (!isOperation) { return }

    if (isInProgress) { return }
    isInProgress = true

    const operation = optionsParam
    setIsLoading(true)
    request(operation, operationParameters)
      .then(({ data }) => setOptions([...data, ...extraOptions]))
      .then(() => setIsLoading(false))
  }, [ optionsParam ])

  useEffect(() => {
    if (!isWatcher) { return }

    const { getOptions } = optionsParam
    const computedOptions = getOptions(form, watchField)

    setOptions(computedOptions)
  }, [ watchField ])

  const extraInputProps = {
    allowClear: true,
    placeholder: DEFAULT_SELECT_PLACEHOLDER,
  }

  if (isMultiple) {
    extraInputProps.mode = "multiple"
    extraInputProps.placeholder = DEFAULT_SELECT_MULTIPLE_PLACEHOLDER
  }

  if (isOperation) {
    extraInputProps.fieldNames = {
      value: 'id',
      label: 'name',
    }
  }

  const validateTrigger = hasAutoComplete ? "onChange" : "onClick"

  const filterOption = (input, option) =>
    (option?.label ?? '')
      .toLowerCase()
      .includes(input.toLowerCase())

  const onChange = value => onSelectChange ? onSelectChange(value, form, options) : {}

  const autoCompleteFilterOptions = (input, option) =>
    (option?.value ?? '').toLowerCase().includes(input.toLowerCase())

  return (
    <Form.Item
      {...itemProps}
      validateTrigger={validateTrigger}
    >
      {
        hasAutoComplete && (
          <AutoComplete
            {...extraInputProps}
            minLength={minLength}
            maxLength={maxLength}
            loading={isLoading}
            options={options}
            disabled={isLoading}
            onChange={onChange}
            showSearch={true}
            filterOption={autoCompleteFilterOptions}
            {...otherInputProps}
          />
        )
      }

      {
        !hasAutoComplete && (
          <Select
            {...extraInputProps}
            loading={isLoading}
            options={options}
            disabled={isLoading}
            onChange={onChange}
            showSearch={true}
            filterOption={filterOption}
            {...otherInputProps}
          />
        )
      }
    </Form.Item>
  )
}

SelectInput.propTypes = {
  form: PropTypes.shape({}).isRequired,
  request: PropTypes.func,
  options: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.string,
    PropTypes.shape(SelectOptionsShape),
    PropTypes.arrayOf(SelectOptionShape),
    PropTypes.arrayOf(PropTypes.string)
  ]).isRequired,
  itemProps: PropTypes.shape({}).isRequired,
  inputProps: PropTypes.shape({}).isRequired,
  isMultiple: PropTypes.bool.isRequired,
  extraOptions: PropTypes.arrayOf(SelectExtraOptionsShape),
}

SelectInput.defaultProps = {
  extraOptions: [],
  request: undefined,
}

export default SelectInput
